import * as React from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import {
  api,
  LocationCreate,
  CreateLocationApiLocationsPostApiArg,
  UpdateLocationApiLocationsLocationIdPutApiArg,
  UpdateLocationApiLocationsLocationIdPutApiResponse,
} from "../../api";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Library } from "@googlemaps/js-api-loader";

// Fix Leaflet default icon issue
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { useJsApiLoader } from "@react-google-maps/api";
import { useAppSelector } from "../../redux/store.ts";
import { addOptionDialogFactory, isAddNewAddressTarget } from "./common.tsx";
import { getCountryName } from "../../helpers/countryNames.ts";
import { isRecord } from "../../api/guards.ts";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Google Maps API key
const googleMapsApiKey = `${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}` || "";
const libraries: Library[] = ["places"];

type LatLng = {
  lat: number;
  lng: number;
};

// Separate MapController component
type MapControllerProps = {
  mapRef: React.RefObject<L.Map | null>;
  position: LatLng | null;
  handleMapClick: (e: L.LeafletMouseEvent) => void;
};

const MapController = ({
  mapRef,
  position,
  handleMapClick,
}: MapControllerProps) => {
  const map = useMap();

  React.useEffect(() => {
    if (map) {
      mapRef.current = map;
    }
  }, [map, mapRef]);

  // Update map center when position changes
  React.useEffect(() => {
    if (map && position) {
      map.setView(position);
    }
  }, [map, position]);

  // Handle map click events
  useMapEvents({
    click: handleMapClick,
  });

  return null;
};

type AddressEditFormProps = {
  formContent: LocationCreate;
  setFormContent: React.Dispatch<React.SetStateAction<LocationCreate>>;
};

const AddressEditForm = ({
  formContent,
  setFormContent,
}: AddressEditFormProps) => {
  const mapRef = React.useRef<L.Map | null>(null);
  const autocompleteContainerRef = React.useRef<HTMLDivElement | null>(null);
  const position = React.useMemo(() => {
    if (formContent.latitude && formContent.longitude) {
      return {
        lat: formContent.latitude,
        lng: formContent.longitude,
      };
    } else {
      return null;
    }
  }, [formContent.latitude, formContent.longitude]);

  const { isLoaded: googleMapsLoaded, loadError: googleMapsLoadError } =
    useJsApiLoader({
      googleMapsApiKey,
      libraries,
      language: "pl",
      region: "PL",
    });

  React.useEffect(() => {
    if (!googleMapsLoaded) {
      return;
    }
    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement(
      {},
    );
    placeAutocomplete.addEventListener("gmp-select", (event) => {
      if (
        "placePrediction" in event &&
        event.placePrediction instanceof google.maps.places.PlacePrediction
      ) {
        // Get the Place object from the prediction
        const place = event.placePrediction.toPlace();

        // Fetch additional fields including address components and geometry
        place
          .fetchFields({
            fields: ["addressComponents", "location", "postalAddress"],
          })
          .then(({ place: placeDetails }) => {
            // Extract country code from address components
            const update: Partial<LocationCreate> = {
              address: place.formattedAddress ?? "",
            };

            for (const component of placeDetails.addressComponents ?? []) {
              if (component.types.includes("country")) {
                update.country = component.shortText ?? "";
              }
              if (component.types.includes("locality")) {
                update.city = component.shortText ?? "";
              }
            }

            // Set coordinates if available
            if (placeDetails.location) {
              update.latitude = placeDetails.location.lat();
              update.longitude = placeDetails.location.lng();
            }

            if (
              "postalAddress" in placeDetails &&
              isRecord(placeDetails.postalAddress) &&
              "addressLines" in placeDetails.postalAddress &&
              "postalCode" in placeDetails.postalAddress &&
              Array.isArray(placeDetails.postalAddress.addressLines) &&
              typeof placeDetails.postalAddress.postalCode === "string"
            ) {
              update.address = [
                ...placeDetails.postalAddress.addressLines.map(
                  (line) => `${line}`,
                ),
                placeDetails.postalAddress.postalCode,
              ].join(", ");
            }

            setFormContent((prev) => ({
              ...prev,
              ...update,
            }));
          })
          .catch((error) => {
            console.error("Error fetching place details:", error);
          });
      }
    });

    autocompleteContainerRef.current?.appendChild?.(placeAutocomplete);
  }, [googleMapsLoaded, setFormContent]);

  const handleMapClick = (e: L.LeafletMouseEvent) => {
    const { lat, lng } = e.latlng;
    setFormContent((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  };

  const clearLocation = () => {
    setFormContent((prev) => ({
      ...prev,
      latitude: null,
      longitude: null,
    }));
  };

  const defaultCenter: LatLng = { lat: 52.2297, lng: 21.0122 }; // Default center (Warsaw, Poland)

  return (
    <>
      <div className="field">
        <label>
          Wyszukiwanie
          <span style={{ color: "red" }}></span>
        </label>
        {googleMapsLoaded ? (
          <div ref={autocompleteContainerRef} className="places-container" />
        ) : null}
        {googleMapsLoadError && (
          <small className="p-error">
            Nie można załadować Google Maps API. Wprowadź adres ręcznie.
          </small>
        )}
      </div>
      <div className="field">
        <label>
          Kraj
          <span style={{ color: "red" }}> *</span>
        </label>
        <InputText
          value={getCountryName(formContent.country)}
          className="w-full"
          readOnly
          disabled
        />
      </div>
      <div className="field">
        <label>
          Miasto<span style={{ color: "red" }}> *</span>
        </label>
        <InputText
          value={formContent.city}
          className="w-full"
          readOnly
          disabled
        />
      </div>
      <div className="field">
        <label>
          Adres<span style={{ color: "red" }}></span>
        </label>
        <InputText
          value={formContent.address}
          onChange={(e) =>
            setFormContent((prev) => ({
              ...prev,
              address: e.target.value,
            }))
          }
          className="w-full"
        />
      </div>
      <div className="field">
        <label>Osoba kontaktowa</label>
        <InputText
          value={formContent.contact_person_name}
          onChange={(e) =>
            setFormContent((prev) => ({
              ...prev,
              contact_person_name: e.target.value,
            }))
          }
          className="w-full"
        />
      </div>
      <div className="field">
        <label>Telefon kontaktowy</label>
        <InputText
          value={formContent.contact_phone_number}
          onChange={(e) =>
            setFormContent((prev) => ({
              ...prev,
              contact_phone_number: e.target.value,
            }))
          }
          className="w-full"
        />
      </div>
      <div className="field">
        <label>Lokalizacja na mapie</label>
        <div style={{ marginBottom: "10px" }}>
          <Button
            label="Wyczyść lokalizację"
            icon="pi pi-times"
            severity="secondary"
            onClick={clearLocation}
            disabled={!position}
            size="small"
          />
        </div>
        <div style={{ height: "400px", width: "100%" }}>
          <MapContainer
            center={position || defaultCenter}
            zoom={position ? 13 : 6}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {position && <Marker position={position} />}
            <MapController
              mapRef={mapRef}
              position={position}
              handleMapClick={handleMapClick}
            />
          </MapContainer>
        </div>
        {position && (
          <div style={{ marginTop: "10px", fontSize: "0.9em", color: "#666" }}>
            Współrzędne: {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
          </div>
        )}
      </div>
    </>
  );
};

const emptyLocation = {
  address: "",
  city: "",
  country: "",
  contact_person_name: "",
  contact_phone_number: "",
  latitude: null,
  longitude: null,
} as const satisfies LocationCreate;

const AddressAddOptionDialog = addOptionDialogFactory<
  LocationCreate,
  | CreateLocationApiLocationsPostApiArg
  | UpdateLocationApiLocationsLocationIdPutApiArg,
  UpdateLocationApiLocationsLocationIdPutApiResponse
>({
  defaultValues: emptyLocation,
  FormComponent: AddressEditForm,
  useMutation: () => {
    const [triggerCreate, createState] =
      api.endpoints.createLocationApiLocationsPost.useMutation();
    const [triggerUpdate, updateState] =
      api.endpoints.updateLocationApiLocationsLocationIdPut.useMutation();

    // Choose the correct mutation based on presence of id
    const trigger = (
      args:
        | CreateLocationApiLocationsPostApiArg
        | UpdateLocationApiLocationsLocationIdPutApiArg,
    ) => {
      if ("locationId" in args && "locationUpdate" in args) {
        return triggerUpdate(args);
      } else {
        return triggerCreate(args);
      }
    };

    return [
      trigger,
      {
        isLoading: createState.isLoading || updateState.isLoading,
        reset: () => {
          createState.reset?.();
          updateState.reset?.();
        },
        originalArgs: undefined,
      },
    ] as const;
  },
  createMutationArg: (formContent) => {
    if ("id" in formContent && formContent.id) {
      // Update
      return {
        locationId: Number(formContent.id),
        locationUpdate: formContent,
      };
    } else {
      // Create
      return {
        locationCreate: formContent,
      };
    }
  },
  validate: (formContent) => !!(formContent.country && formContent.city),
  headerText: "Dodaj adres",
});

export const AddressAddOption: React.FC = () => {
  const { target } = useAppSelector((state) => state.addNew);
  if (!isAddNewAddressTarget(target)) {
    return null;
  }
  return (
    <AddressAddOptionDialog
      rowId={target.id}
      field={target.field}
      initialValues={target.initialValues}
    />
  );
};
